import { useTheme } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AsyncAutocomplete } from '@pro4-erp/core-web';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Info as InfoIcon } from '@mui/icons-material/';
import { getChangedFormValues, getLocalDateTimeFromUtc } from '@/core';
import { CostList, TeamCost } from '../types';
import { getCostLists } from '../api/getCostLists';

type CostFormType = {
  info: string;
  note: string;
  datReg: string;
  price: string;
  costList: CostList | null;
  travelFrom: Date | string;
  travelTo: Date | string;
  amount: string;
  proposedQ: string;
  lunchFactor: boolean;
};

type CostModalProps = {
  title: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSubmit: (data: Partial<CostFormType>) => void;
  teamCost?: TeamCost;
  minDate?: string | undefined;
  maxDate?: string | undefined;
};

const CostModal = ({
  title,
  isOpen,
  setIsOpen,
  onSubmit,
  teamCost,
  minDate,
  maxDate,
}: CostModalProps) => {
  const theme = useTheme();
  const [showInfo, setShowInfo] = useState(false);
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { t, i18n } = useTranslation();


  const form = useForm<CostFormType>({
    mode: 'onTouched',
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
      setShowInfo(false);
    }
  }, [isOpen, form]);

  const getDefaultDate = (mainVal: string | undefined, defaultVal: string | undefined) => {
    if (mainVal) {
      return getLocalDateTimeFromUtc(mainVal);
    }
    if (defaultVal) {
      return getLocalDateTimeFromUtc(
        dayjs(defaultVal).subtract(2, 'hour').format('YYYY-MM-DDTHH:mm:ss')
      );
    }
    return undefined;
  };

  useEffect(() => {
    form.reset({
      note: teamCost?.note,
      datReg: teamCost?.datReg ? new Date(teamCost.datReg).toLocaleDateString('en-CA') : undefined,
      price: teamCost?.price,
      amount: teamCost?.amount,
      proposedQ: teamCost?.proposedQ,
      costList: teamCost?.costList || null,
      travelFrom: getDefaultDate(teamCost?.travelFrom, minDate),
      travelTo: getDefaultDate(teamCost?.travelTo, maxDate),
      lunchFactor: !!teamCost?.lunchFactor,
    });
  }, [teamCost, form, minDate, maxDate]);

  const handleClose = () => {
    form.reset();
    setIsOpen(false);
  };

  const baseSubmit = (data: CostFormType) => {
    if (Object.keys(form.formState.dirtyFields).length === 0 && data.costList?.ident !== 'DNV') {
      handleClose();
      return;
    }
    if (!data.costList) {
      form.setError('costList', { message: 'Tip je obvezen' }, { shouldFocus: true });
      return;
    }

    onSubmit(getChangedFormValues(form.formState.dirtyFields, data));
  };
  const getOpisLabel = (costName: string | undefined, isDaily: boolean | undefined): string => {
    if (isDaily)
      return t('Describe the number of per diems', {
        defaultValue: 'Describe the number of per diems',
      });
    if (costName === 'Prevoženi kilometri') return t('Relation', { defaultValue: 'Relation' });
    if (costName === 'Dnevnice')
      return t('Describe the number of per diems', {
        defaultValue: 'Describe the number of per diems',
      });
    return t('Note', { defaultValue: 'Note' });
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
      fullWidth
    >
      <DialogTitle id="responsive-dialog-title">
        {title}
        {teamCost?.costList?.isDaily && (
          <Button onClick={() => setShowInfo((prev) => !prev)}>
            <InfoIcon />
          </Button>
        )}
        {showInfo && (
          <Alert severity="info">
            <Typography>Matična država: dnevnice: 8- 12 ur: 1/2; nad 12 ur: 1</Typography>
            <Typography>Tujina: dnevnice: 8- 14 ur: 1/2; nad 14 ur: 1</Typography>
          </Alert>
        )}
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          onSubmit={form.handleSubmit(baseSubmit)}
          noValidate
          sx={{ mt: '1rem' }}
        >
          <Box sx={{ mt: '1rem' }}>
            {teamCost?.costList?.ident !== '_KMT' &&
            teamCost?.costList?.ident !== '_U_I' &&
            teamCost?.costList?.ident !== '_DNV' ? (
              <AsyncAutocomplete<CostList>
                id="cost-list"
                label={t('Type', { defaultValue: 'Type' })}
                fullWidth
                getData={async (search) => {
                  const response = await getCostLists({
                    page: 1,
                    pageSize: 1000,
                    search,
                    lang: i18n.language,
                  });

                  return response.results;
                }}
                getOptionLabel={(option) => option.name || 'N/A'}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                error={!!form.formState.errors.costList}
                helperText={form.formState.errors.costList?.message?.toString()}
                selectedOption={form.getValues('costList')}
                setSelectedOption={(option) => {
                  form.setValue('costList', option, { shouldValidate: true, shouldDirty: true });
                }}
              />
            ) : (
              <> </>
            )}
          </Box>

          <Controller
            control={form.control}
            name="note"
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ mt: '1rem' }}
                label={getOpisLabel(
                  form.getValues('costList.name'),
                  form.getValues('costList')?.isDaily
                )}
                fullWidth
                error={!!form.formState.errors.note}
                helperText={form.formState.errors.note?.message?.toString()}
              />
            )}
          />

          {/* {!form.getValues('costList')?.isDaily && (
            <Controller
              control={form.control}
              name="price"
              render={({ field }) => (
                <TextField
                  {...field}
                  sx={{ mt: '1rem' }}
                  label={t('Price', { defaultValue: 'Price' })}
                  fullWidth
                  error={!!form.formState.errors.price}
                  helperText={form.formState.errors.price?.message?.toString()}
                  type="number"
                  inputProps={{ step: 0.01 }}
                />
              )}
            />
          )} */}

          {!form.getValues('costList')?.isDaily && !form.getValues('costList')?.forAuditor && (
            <Controller
              control={form.control}
              name="proposedQ"
              rules={{
                min: 0,
                required:
                  form.getValues('costList')?.ident === '_U_I' ? 'Potrebna je količina' : false,
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  sx={{ mt: '1rem' }}
                  label={
                    t('Proposal amount', { defaultValue: 'Proposal amount' }) +
                    (form.getValues('costList')?.ident === '_KMT' ? ' (km)' : '') +
                    (form.getValues('costList')?.ident === '_U_I' ? ' (ure)' : '')
                  }
                  fullWidth
                  error={!!form.formState.errors.proposedQ}
                  helperText={form.formState.errors.proposedQ?.message?.toString()}
                  type="number"
                  inputProps={{ step: 0.01 }}
                />
              )}
            />
          )}
          {!form.getValues('costList')?.isDaily && form.getValues('costList')?.forAuditor && (
            <Controller
              control={form.control}
              name="price"
              render={({ field }) => (
                <TextField
                  {...field}
                  sx={{ mt: '1rem' }}
                  label={t('Price', { defaultValue: 'Price' })}
                  fullWidth
                  error={!!form.formState.errors.amount}
                  helperText={form.formState.errors.amount?.message?.toString()}
                  type="number"
                  inputProps={{ step: 0.01 }}
                />
              )}
            />
          )}

          {!!form.getValues('costList')?.isDaily && (
            <>
              <Controller
                control={form.control}
                name="travelFrom"
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      {...field}
                      value={dayjs(field.value ? field.value : null)}
                      minDate={dayjs(minDate)}
                      maxDate={dayjs(maxDate)}
                      sx={{ mt: '1rem', width: '100%' }}
                      label={t('Travel from', { defaultValue: 'Travel from' })}
                      format="YYYY-MM-DD HH:mm"
                      ampm={false}
                    />
                  </LocalizationProvider>
                )}
              />

              <Controller
                control={form.control}
                name="travelTo"
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      {...field}
                      value={dayjs(field.value ? field.value : null)}
                      sx={{ mt: '1rem', width: '100%' }}
                      minDate={dayjs(minDate)}
                      maxDate={dayjs(maxDate)}
                      label={t('Travel to', { defaultValue: 'Travel to' })}
                      format="YYYY-MM-DD HH:mm"
                      ampm={false}
                    />
                  </LocalizationProvider>
                )}
              />
              <FormControlLabel
                label={t('Lunch', { defaultValue: 'Lunch' })}
                control={
                  <Controller
                    control={form.control}
                    name="lunchFactor"
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
              />
            </>
          )}

          <Button variant="contained" type="submit" fullWidth sx={{ mt: '1rem' }}>
            {t('Submit', { defaultValue: 'Submit' })}
          </Button>

          <Button variant="outlined" fullWidth sx={{ mt: '1rem' }} onClick={handleClose}>
            {t('Cancel', { defaultValue: 'Cancel' })}
          </Button>

          {!!form.formState.errors.root && (
            <FormHelperText error>{form.formState.errors.root.message}</FormHelperText>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CostModal;
